import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

const normalizeJob = (raw) => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const status = typeof raw.status === 'string' ? raw.status.toUpperCase() : 'PENDING';
  const completed = Boolean(raw.completed) || status === 'COMPLETE' || status === 'ERROR';
  const error = Boolean(raw.error) || status === 'ERROR';
  const progressValue = Number(raw.progress);
  const progress = Number.isFinite(progressValue)
    ? Math.max(0, Math.min(100, Math.round(progressValue)))
    : 0;

  return {
    ...raw,
    status,
    completed,
    error,
    progress,
    active: !completed && !error,
    message: typeof raw.message === 'string' ? raw.message : '',
  };
};

const hasNonEmptyTestCaseRows = (rows) => {
  if (!Array.isArray(rows)) {
    return false;
  }
  return rows.some((row) => {
    if (!row || typeof row !== 'object') {
      return false;
    }
    return Object.values(row).some((value) => String(value ?? '').trim().length > 0);
  });
};

const extractFileName = (headers, fallback) => {
  const disposition = headers?.['content-disposition'];
  if (typeof disposition !== 'string') {
    return fallback;
  }
  const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
  if (!match) {
    return fallback;
  }
  const encoded = match[1] || match[2];
  try {
    return decodeURIComponent(encoded);
  } catch (error) {
    return encoded || fallback;
  }
};

const downloadBlob = (blobData, filename) => {
  const blob = blobData instanceof Blob
    ? blobData
    : new Blob([blobData ?? new ArrayBuffer(0)]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const splitMarkdownCells = (line) => {
  const trimmed = String(line ?? '').trim().replace(/^\|/, '').replace(/\|$/, '');
  if (trimmed.length === 0) {
    return [];
  }

  const cells = [];
  let current = '';
  let escaped = false;

  for (let index = 0; index < trimmed.length; index += 1) {
    const char = trimmed[index];
    if (escaped) {
      current += char;
      escaped = false;
      continue;
    }
    if (char === '\\') {
      escaped = true;
      continue;
    }
    if (char === '|') {
      cells.push(current.trim());
      current = '';
      continue;
    }
    current += char;
  }
  cells.push(current.trim());
  return cells;
};

const isTableSeparator = (line) => {
  const cells = String(line ?? '')
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim())
    .filter((cell) => cell.length > 0);

  return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
};

const isHeadingLine = (line) => /^(#{1,6})\s+/.test(line);
const isUnorderedListLine = (line) => /^[-*]\s+/.test(line);
const isOrderedListLine = (line) => /^\d+\.\s+/.test(line);

const parseReportMarkdown = (reportText) => {
  if (typeof reportText !== 'string' || reportText.trim().length === 0) {
    return [];
  }

  const lines = reportText.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let index = 0;

  while (index < lines.length) {
    const rawLine = lines[index];
    const line = rawLine.trim();

    if (line.length === 0) {
      index += 1;
      continue;
    }

    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      blocks.push({
        type: 'heading',
        level: headingMatch[1].length,
        text: headingMatch[2].trim(),
      });
      index += 1;
      continue;
    }

    if (line.startsWith('|') && index + 1 < lines.length && isTableSeparator(lines[index + 1].trim())) {
      const headers = splitMarkdownCells(line);
      index += 2;

      const rows = [];
      while (index < lines.length) {
        const tableLine = lines[index].trim();
        if (!tableLine.startsWith('|')) {
          break;
        }
        if (isTableSeparator(tableLine)) {
          index += 1;
          continue;
        }
        const rowCells = splitMarkdownCells(tableLine);
        if (rowCells.some((cell) => cell.length > 0)) {
          const normalized = headers.map((_, columnIndex) => rowCells[columnIndex] ?? '');
          rows.push(normalized);
        }
        index += 1;
      }

      blocks.push({
        type: 'table',
        headers,
        rows,
      });
      continue;
    }

    if (isUnorderedListLine(line)) {
      const items = [];
      while (index < lines.length) {
        const listLine = lines[index].trim();
        const match = listLine.match(/^[-*]\s+(.+)$/);
        if (!match) {
          break;
        }
        items.push(match[1].trim());
        index += 1;
      }
      blocks.push({ type: 'unorderedList', items });
      continue;
    }

    if (isOrderedListLine(line)) {
      const items = [];
      while (index < lines.length) {
        const listLine = lines[index].trim();
        const match = listLine.match(/^\d+\.\s+(.+)$/);
        if (!match) {
          break;
        }
        items.push(match[1].trim());
        index += 1;
      }
      blocks.push({ type: 'orderedList', items });
      continue;
    }

    const paragraphLines = [line];
    index += 1;
    while (index < lines.length) {
      const nextLine = lines[index].trim();
      if (nextLine.length === 0
        || isHeadingLine(nextLine)
        || isUnorderedListLine(nextLine)
        || isOrderedListLine(nextLine)
        || (nextLine.startsWith('|') && index + 1 < lines.length && isTableSeparator(lines[index + 1].trim()))) {
        break;
      }
      paragraphLines.push(nextLine);
      index += 1;
    }
    blocks.push({
      type: 'paragraph',
      text: paragraphLines.join(' '),
    });
  }

  return blocks;
};

const renderHeading = (block, blockIndex) => {
  const level = Number(block.level) || 2;
  const text = block.text || '';
  if (level <= 1) {
    return <Typography key={`report-heading-${blockIndex}`} variant="h5">{text}</Typography>;
  }
  if (level === 2) {
    return <Typography key={`report-heading-${blockIndex}`} variant="h6">{text}</Typography>;
  }
  return <Typography key={`report-heading-${blockIndex}`} variant="subtitle1">{text}</Typography>;
};

export default function StepFiveSummaryReportPanel({
  projectId,
  apiClient,
  disabled = false,
  disabledReason = null,
}) {
  const [reportJob, setReportJob] = useState(null);
  const [reportArtifact, setReportArtifact] = useState(null);
  const [reportText, setReportText] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [reportSaving, setReportSaving] = useState(false);
  const [reportDownloading, setReportDownloading] = useState(false);
  const [sourceTestCasesReady, setSourceTestCasesReady] = useState(false);
  const [reportViewMode, setReportViewMode] = useState('preview');

  const hasReportArtifact = Boolean(reportArtifact?.id);
  const reportBlocks = useMemo(() => parseReportMarkdown(reportText), [reportText]);

  const applyReportArtifact = useCallback((artifact) => {
    setReportArtifact(artifact || null);
    setReportText(typeof artifact?.reportText === 'string' ? artifact.reportText : '');
  }, []);

  const loadReportArtifactByJob = useCallback(async (jobId) => {
    if (!jobId) {
      return;
    }
    const response = await apiClient.get(`/api/projects/${projectId}/story-qa/summary-reports/jobs/${jobId}/artifact`);
    applyReportArtifact(response?.data ?? null);
  }, [apiClient, applyReportArtifact, projectId]);

  const refreshSourceState = useCallback(async () => {
    try {
      const response = await apiClient.get(`/api/projects/${projectId}/story-qa/test-cases/artifacts/latest`);
      const ready = hasNonEmptyTestCaseRows(response?.data?.rows);
      setSourceTestCasesReady(ready);
      return ready;
    } catch (error) {
      setSourceTestCasesReady(false);
      return false;
    }
  }, [apiClient, projectId]);

  const pollReportJob = useCallback(async (jobId) => {
    try {
      const response = await apiClient.get(`/api/projects/${projectId}/story-qa/summary-reports/jobs/${jobId}`);
      const normalized = normalizeJob(response?.data);
      if (!normalized) {
        return;
      }
      setReportJob(normalized);
      if (normalized.completed && !normalized.error) {
        await loadReportArtifactByJob(normalized.jobId);
      }
    } catch (error) {
      setReportError(error?.response?.data?.error || error.message || 'Не удалось получить статус генерации сводного отчета.');
    }
  }, [apiClient, loadReportArtifactByJob, projectId]);

  const loadInitialState = useCallback(async () => {
    setReportLoading(true);
    setReportError(null);

    try {
      const [reportArtifactRes, reportActiveRes, sourceRes] = await Promise.allSettled([
        apiClient.get(`/api/projects/${projectId}/story-qa/summary-reports/artifacts/latest`),
        apiClient.get(`/api/projects/${projectId}/story-qa/summary-reports/jobs/active`),
        apiClient.get(`/api/projects/${projectId}/story-qa/test-cases/artifacts/latest`),
      ]);

      if (reportArtifactRes.status === 'fulfilled') {
        applyReportArtifact(reportArtifactRes.value?.data ?? null);
      }

      if (reportActiveRes.status === 'fulfilled') {
        setReportJob(normalizeJob(reportActiveRes.value?.data));
      }

      if (sourceRes.status === 'fulfilled') {
        setSourceTestCasesReady(hasNonEmptyTestCaseRows(sourceRes.value?.data?.rows));
      } else {
        setSourceTestCasesReady(false);
      }
    } catch (error) {
      setReportError(error?.response?.data?.error || error.message || 'Не удалось загрузить данные шага 5.');
    } finally {
      setReportLoading(false);
    }
  }, [apiClient, applyReportArtifact, projectId]);

  useEffect(() => {
    setReportJob(null);
    setReportArtifact(null);
    setReportText('');
    setSourceTestCasesReady(false);
    loadInitialState();
  }, [loadInitialState, projectId]);

  useEffect(() => {
    if (!reportJob?.active || !reportJob?.jobId) {
      return undefined;
    }

    const timer = setInterval(() => {
      pollReportJob(reportJob.jobId);
    }, 2000);

    return () => clearInterval(timer);
  }, [pollReportJob, reportJob?.active, reportJob?.jobId]);

  const handleGenerateReport = useCallback(async () => {
    if (disabled || reportJob?.active) {
      return;
    }

    setReportError(null);
    const sourceReady = await refreshSourceState();
    if (!sourceReady) {
      setReportError('Сначала сгенерируйте тест-кейсы на шаге 3.');
      return;
    }

    try {
      const response = await apiClient.post(`/api/projects/${projectId}/story-qa/summary-reports/generate`);
      const normalized = normalizeJob(response?.data);
      setReportJob(normalized);
      if (normalized?.completed && !normalized.error) {
        await loadReportArtifactByJob(normalized.jobId);
      }
    } catch (error) {
      setReportError(error?.response?.data?.error || error.message || 'Не удалось запустить генерацию сводного отчета.');
    }
  }, [apiClient, disabled, loadReportArtifactByJob, projectId, refreshSourceState, reportJob?.active]);

  const handleSaveReport = useCallback(async () => {
    if (!hasReportArtifact || reportSaving) {
      return;
    }

    setReportSaving(true);
    setReportError(null);
    try {
      const response = await apiClient.put(
        `/api/projects/${projectId}/story-qa/summary-reports/artifacts/${reportArtifact.id}`,
        { reportText }
      );
      applyReportArtifact(response?.data ?? null);
    } catch (error) {
      setReportError(error?.response?.data?.error || error.message || 'Не удалось сохранить сводный отчет.');
    } finally {
      setReportSaving(false);
    }
  }, [apiClient, applyReportArtifact, hasReportArtifact, projectId, reportArtifact?.id, reportSaving, reportText]);

  const handleDownloadReport = useCallback(async () => {
    if (!hasReportArtifact || reportDownloading) {
      return;
    }

    setReportDownloading(true);
    setReportError(null);
    try {
      const response = await apiClient.get(
        `/api/projects/${projectId}/story-qa/summary-reports/artifacts/${reportArtifact.id}/download`,
        { responseType: 'blob' }
      );
      const filename = extractFileName(response?.headers, 'qa-summary-report.md');
      downloadBlob(response?.data, filename);
    } catch (error) {
      setReportError(error?.response?.data?.error || error.message || 'Не удалось скачать сводный отчет.');
    } finally {
      setReportDownloading(false);
    }
  }, [apiClient, hasReportArtifact, projectId, reportArtifact?.id, reportDownloading]);

  const reportStatus = useMemo(() => {
    if (reportJob?.active) {
      return { label: 'В процессе', color: 'info' };
    }
    if (reportJob?.error) {
      return { label: 'Ошибка', color: 'error' };
    }
    if (hasReportArtifact) {
      return { label: 'Готово', color: 'success' };
    }
    return { label: 'Не запущено', color: 'default' };
  }, [hasReportArtifact, reportJob?.active, reportJob?.error]);

  const handleViewModeChange = useCallback((event, nextValue) => {
    if (nextValue) {
      setReportViewMode(nextValue);
    }
  }, []);

  return (
    <Paper className="surface-glow" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Typography variant="h6">Шаг 5. Сводный QA отчет</Typography>
          <Chip size="small" label={`Сводный отчет: ${reportStatus.label}`} color={reportStatus.color} variant="outlined" />
        </Stack>

        {disabled && (
          <Alert severity="info">
            {disabledReason || 'LLM-операции временно недоступны.'}
          </Alert>
        )}

        {!reportLoading && !sourceTestCasesReady && (
          <Alert severity="info">
            Для генерации сводного отчета сначала подготовьте тест-кейсы на шаге 3.
          </Alert>
        )}

        {reportLoading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Загрузка данных сводного отчета…</Typography>
          </Stack>
        ) : null}

        {reportError && <Alert severity="error">{reportError}</Alert>}

        {reportJob?.active && (
          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              {reportJob.message || 'Генерация сводного отчета в процессе…'}
            </Typography>
            <LinearProgress variant="determinate" value={reportJob.progress || 0} />
          </Stack>
        )}

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1">Сводный отчет по качеству и покрытию</Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={handleGenerateReport}
            disabled={disabled || reportJob?.active || reportLoading}
          >
            {reportJob?.active ? 'Генерация…' : 'Сгенерировать'}
          </Button>
        </Stack>

        <Paper variant="outlined" sx={{ p: 1 }}>
          <Tabs value={reportViewMode} onChange={handleViewModeChange}>
            <Tab value="preview" label="Отчет" />
            <Tab value="edit" label="Редактирование" />
          </Tabs>
        </Paper>

        {reportViewMode === 'preview' ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            {reportBlocks.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Отчет пока пуст. Запустите генерацию или откройте режим редактирования.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {reportBlocks.map((block, blockIndex) => {
                  if (block.type === 'heading') {
                    return renderHeading(block, blockIndex);
                  }
                  if (block.type === 'paragraph') {
                    return (
                      <Typography key={`report-paragraph-${blockIndex}`} variant="body2">
                        {block.text}
                      </Typography>
                    );
                  }
                  if (block.type === 'unorderedList') {
                    return (
                      <Stack
                        key={`report-ul-${blockIndex}`}
                        component="ul"
                        spacing={0.5}
                        sx={{ m: 0, pl: 3 }}
                      >
                        {block.items.map((item, itemIndex) => (
                          <Typography
                            key={`report-ul-item-${blockIndex}-${itemIndex}`}
                            component="li"
                            variant="body2"
                          >
                            {item}
                          </Typography>
                        ))}
                      </Stack>
                    );
                  }
                  if (block.type === 'orderedList') {
                    return (
                      <Stack
                        key={`report-ol-${blockIndex}`}
                        component="ol"
                        spacing={0.5}
                        sx={{ m: 0, pl: 3 }}
                      >
                        {block.items.map((item, itemIndex) => (
                          <Typography
                            key={`report-ol-item-${blockIndex}-${itemIndex}`}
                            component="li"
                            variant="body2"
                          >
                            {item}
                          </Typography>
                        ))}
                      </Stack>
                    );
                  }
                  if (block.type === 'table') {
                    return (
                      <Paper key={`report-table-${blockIndex}`} variant="outlined" sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {block.headers.map((header, headerIndex) => (
                                <TableCell key={`report-th-${blockIndex}-${headerIndex}`}>
                                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                    {header}
                                  </Typography>
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {block.rows.map((row, rowIndex) => (
                              <TableRow key={`report-tr-${blockIndex}-${rowIndex}`}>
                                {block.headers.map((_, columnIndex) => (
                                  <TableCell key={`report-td-${blockIndex}-${rowIndex}-${columnIndex}`}>
                                    <Typography variant="body2">
                                      {row[columnIndex] || ''}
                                    </Typography>
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Paper>
                    );
                  }
                  return null;
                })}
              </Stack>
            )}
          </Paper>
        ) : (
          <TextField
            label="Текст сводного отчета (Markdown)"
            value={reportText}
            onChange={(event) => setReportText(event.target.value)}
            multiline
            minRows={18}
            fullWidth
          />
        )}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={handleSaveReport}
            disabled={!hasReportArtifact || reportSaving}
          >
            {reportSaving ? 'Сохранение…' : 'Сохранить'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleDownloadReport}
            disabled={!hasReportArtifact || reportDownloading}
          >
            {reportDownloading ? 'Скачивание…' : 'Скачать MD'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
